import { computed, ref } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

const DEBT_STATES = ['ESTAGNADO', 'SRETORNO', 'CONCLUIDA', 'LIXO'];

// debt_confidence is a categorical string emitted by the backend classifier
// (alta | media | baixa). There is no numeric threshold.
const CONFIDENCE_I18N_KEY = {
  alta: 'HIGH',
  media: 'MEDIUM',
  baixa: 'LOW',
};

const SOURCE_I18N_KEY = {
  human: 'HUMAN',
  auto: 'AUTO',
};

/**
 * Centralizes the debt-state feedback widget logic shared by DebtStateStrip
 * and DebtStateBlock: reading the classified state from the selected chat,
 * resolving i18n labels, holding the inline edit state, and dispatching
 * confirm/correct feedback to the store.
 *
 * @returns {Object} computeds, label helpers, edit state and feedback actions.
 */
export function useDebtStateFeedback() {
  const store = useStore();
  const { t } = useI18n();
  const { accountId } = useAccount();
  const currentChat = useMapGetter('getSelectedChat');
  const isFeatureEnabledonAccount = useMapGetter(
    'accounts/isFeatureEnabledonAccount'
  );

  const conversationId = computed(() => currentChat.value?.id);
  const debtState = computed(() => currentChat.value?.debt_state || '');
  const debtConfidence = computed(
    () => currentChat.value?.debt_confidence || ''
  );
  const debtStateSource = computed(
    () => currentChat.value?.debt_state_source || ''
  );
  const debtStates = computed(() => DEBT_STATES);

  const isFeatureEnabled = computed(() => {
    if (!accountId.value) return false;
    return isFeatureEnabledonAccount.value(
      accountId.value,
      FEATURE_FLAGS.DEBT_STATE_WIDGET
    );
  });

  const hasState = computed(
    () => isFeatureEnabled.value && Boolean(debtState.value)
  );

  // === i18n label helpers ===
  // Interpolate the STATES.* key directly, guarding against an unknown state
  // (the backend is the source of truth for the enum); fall back to the raw
  // value so a future/unknown state never renders an empty label.
  const stateLabel = computed(() => {
    if (!debtStates.value.includes(debtState.value)) return debtState.value;
    return t(`CONVERSATION.DEBT_STATE_WIDGET.STATES.${debtState.value}`);
  });

  const stateOptionLabel = state => {
    if (!debtStates.value.includes(state)) return state;
    return t(`CONVERSATION.DEBT_STATE_WIDGET.STATES.${state}`);
  };

  const confidenceLabel = computed(() => {
    const key = CONFIDENCE_I18N_KEY[debtConfidence.value];
    if (!key) return '';
    return t(`CONVERSATION.DEBT_STATE_WIDGET.CONFIDENCE.${key}`);
  });

  const sourceLabel = computed(() => {
    const key = SOURCE_I18N_KEY[debtStateSource.value];
    if (!key) return '';
    return t(`CONVERSATION.DEBT_STATE_WIDGET.SOURCE.${key}`);
  });

  // === Inline edit state ===
  const isCorrecting = ref(false);
  const selectedState = ref('');
  const isSubmitting = ref(false);

  const startCorrect = () => {
    isCorrecting.value = true;
    selectedState.value = '';
  };

  const cancelCorrect = () => {
    isCorrecting.value = false;
    selectedState.value = '';
  };

  const resetEdit = () => {
    isCorrecting.value = false;
    selectedState.value = '';
  };

  // === Feedback actions ===
  // predicted* carry the label EXACTLY as displayed when the agent judged it;
  // the backend stores them as annotation (never as scope/authorization).
  /**
   * Confirms the predicted state.
   * @returns {Promise<boolean>} true on success so the caller can react
   *   (e.g. the strip dismisses itself; the block does not).
   */
  const confirm = async () => {
    if (!conversationId.value) return false;
    if (isSubmitting.value) return false;
    isSubmitting.value = true;
    try {
      await store.dispatch('sendDebtStateFeedback', {
        conversationId: conversationId.value,
        action: 'confirm',
        predictedState: debtState.value,
        predictedConfidence: debtConfidence.value,
      });
      useAlert(t('CONVERSATION.DEBT_STATE_WIDGET.SUCCESS_CONFIRM'));
      return true;
    } catch (error) {
      useAlert(t('CONVERSATION.DEBT_STATE_WIDGET.ERROR'));
      return false;
    } finally {
      isSubmitting.value = false;
    }
  };

  /**
   * Submits a corrected state. Validates the selection against the known enum.
   * @returns {Promise<boolean>} true on success.
   */
  const submitCorrect = async () => {
    if (!conversationId.value) return false;
    if (!debtStates.value.includes(selectedState.value)) return false;
    if (isSubmitting.value) return false;
    isSubmitting.value = true;
    try {
      await store.dispatch('sendDebtStateFeedback', {
        conversationId: conversationId.value,
        action: 'correct',
        correctedState: selectedState.value,
        predictedState: debtState.value,
        predictedConfidence: debtConfidence.value,
      });
      useAlert(t('CONVERSATION.DEBT_STATE_WIDGET.SUCCESS_CORRECT'));
      isCorrecting.value = false;
      return true;
    } catch (error) {
      useAlert(t('CONVERSATION.DEBT_STATE_WIDGET.ERROR'));
      return false;
    } finally {
      isSubmitting.value = false;
    }
  };

  return {
    accountId,
    conversationId,
    debtState,
    debtConfidence,
    debtStateSource,
    debtStates,
    isFeatureEnabled,
    hasState,
    stateLabel,
    stateOptionLabel,
    confidenceLabel,
    sourceLabel,
    isCorrecting,
    selectedState,
    isSubmitting,
    confirm,
    startCorrect,
    cancelCorrect,
    submitCorrect,
    resetEdit,
  };
}
